import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout'; // Adjust path as necessary

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

function BoostHistoryPage() {
  const [boostHistory, setBoostHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null); // To pass to Layout
  const [paginationInfo, setPaginationInfo] = useState(null);

  // Fetch current user for Layout
  const fetchCurrentUser = useCallback(async () => {
    if (!BACKEND_API_URL) return;
    try {
      const response = await fetch(`${BACKEND_API_URL}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
        }
      }
    } catch (e) {
      console.error("Failed to fetch current user for Layout", e);
    }
  }, []);

  const fetchBoostHistory = useCallback(async () => {
    if (!BACKEND_API_URL) {
      setError("Backend API URL is not configured.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/boost-history`, {
        credentials: 'include', // Important for authenticated requests
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const result = await response.json();
      if (result.success && result.data && result.data.boosts) {
        setBoostHistory(result.data.boosts); // Boosts are already sorted by API
        setPaginationInfo({
            currentPage: result.data.current_page,
            pageCount: result.data.page_count,
            boostsCount: result.data.boosts_count,
        });
      } else {
        throw new Error(result.message || 'Failed to fetch boost history data.');
      }
    } catch (err) {
      console.error("Error fetching boost history:", err);
      setError(err.message);
      setBoostHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchBoostHistory();
  }, [fetchCurrentUser, fetchBoostHistory]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <Layout user={user}><div className="text-center p-10">Loading boost history...</div></Layout>;
  }

  return (
    <Layout user={user}>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-semibold mb-6 text-center">Server Boost History</h1>
        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}
        
        {paginationInfo && (
            <div className="mb-4 text-sm text-gray-400">
                Page {paginationInfo.currentPage} of {paginationInfo.pageCount} ({paginationInfo.boostsCount} total boosts)
            </div>
        )}

        <div className="overflow-x-auto bg-gray-800 bg-opacity-75 shadow-md rounded-lg">
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="border-b-2 border-gray-700">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Avatar</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Username</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Message</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Boosted At</th>
              </tr>
            </thead>
            <tbody>
              {boostHistory.length > 0 ? boostHistory.map((boost) => (
                <tr key={boost.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors duration-150">
                  <td className="px-5 py-4 text-sm"><img src={boost.avatar} alt={`${boost.username}'s avatar`} className="w-10 h-10 rounded-full" onError={(e) => e.target.src='https://placehold.co/40x40/777/eee?text=N/A'} /></td>
                  <td className="px-5 py-4 text-sm text-gray-200">{boost.username}</td>
                  <td className="px-5 py-4 text-sm text-gray-200">{boost.amount}</td>
                  <td className="px-5 py-4 text-sm text-gray-200 max-w-xs truncate" title={boost.message || ''}>{boost.message || <span className="text-gray-500 italic">No message</span>}</td>
                  <td className="px-5 py-4 text-sm text-gray-200">{formatDate(boost.boosted_at)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="text-center py-10 text-gray-400">No boost history found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default BoostHistoryPage;